import React, { useState } from "react";
import { Plus, Search, Filter, Users, Clock, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useArtistDetails, type ArtistDetail, type CreateArtistDetailData, type UpdateArtistDetailData } from "@/hooks/useArtistDetails";
import ArtistDetailsTable from "./ArtistDetailsTable";
import ViewArtistDetail from "./ViewArtistDetail";
import { ArtistForm } from "../ArtistManagement/ArtistForm";

export default function ArtistDetailsManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingArtistDetail, setViewingArtistDetail] = useState<ArtistDetail | null>(null);
  const [editingArtistDetail, setEditingArtistDetail] = useState<ArtistDetail | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const { toast } = useToast();

  const {
    artistDetails,
    loading,
    error,
    createArtistDetail,
    updateArtistDetail,
    deleteArtistDetail,
    updateStatus,
    searchArtistDetails,
    filterByStatus,
    getStatistics,
  } = useArtistDetails();

  const statistics = getStatistics();

  const handleView = (artistDetail: ArtistDetail) => {
    setViewingArtistDetail(artistDetail);
    setIsViewMode(true);
    setIsDialogOpen(true);
  };

  const handleEdit = (artistDetail: ArtistDetail) => {
    setEditingArtistDetail(artistDetail);
    setIsViewMode(false);
    setIsDialogOpen(true);
  };

  const handleDelete = async (artistDetailId: string) => {
    if (window.confirm("Are you sure you want to delete this artist detail?")) {
      const success = await deleteArtistDetail(artistDetailId);
      if (success) {
        toast({
          title: "Success",
          description: "Artist detail deleted successfully",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to delete artist detail",
          variant: "destructive",
        });
      }
    }
  };

  const handleStatusChange = async (artistDetailId: string, status: ArtistDetail['status']) => {
    const success = await updateStatus(artistDetailId, status);
    if (success) {
      toast({
        title: "Success",
        description: `Status updated to ${status}`,
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (data: CreateArtistDetailData | UpdateArtistDetailData) => {
    let success = false;
    
    if (editingArtistDetail) {
      const updated = await updateArtistDetail(editingArtistDetail.id, data as UpdateArtistDetailData);
      success = !!updated;
      if (success) {
        toast({
          title: "Success",
          description: "Artist detail updated successfully",
        });
      }
    } else {
      const created = await createArtistDetail(data as CreateArtistDetailData);
      success = !!created;
      if (success) {
        toast({
          title: "Success",
          description: "Artist detail created successfully",
        });
      }
    }

    if (success) {
      setIsDialogOpen(false);
      resetForm();
    } else {
      toast({
        title: "Error",
        description: editingArtistDetail ? "Failed to update artist detail" : "Failed to create artist detail",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setEditingArtistDetail(null);
    setViewingArtistDetail(null);
    setIsViewMode(false);
  };

  const filteredArtistDetails = artistDetails.filter((artistDetail) => {
    const matchesSearch = searchTerm === "" || 
      artistDetail.artistid.toLowerCase().includes(searchTerm.toLowerCase()) ||
      artistDetail.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      artistDetail.email_address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      artistDetail.city_state?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || artistDetail.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-red-800">Error</h3>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Artist Details Management</h1>
          <p className="text-gray-600">Manage artist information and applications</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => {
              setIsViewMode(false);
              setEditingArtistDetail(null);
              setViewingArtistDetail(null);
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Add New Artist Detail
            </Button>
          </DialogTrigger>
          <DialogContent className={`${isViewMode ? 'max-w-6xl' : 'max-w-4xl'} max-h-[90vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle>
                {isViewMode && viewingArtistDetail 
                  ? `View Artist Detail - ${viewingArtistDetail.full_name}`
                  : editingArtistDetail 
                    ? "Edit Artist Detail" 
                    : "Add New Artist Detail"
                }
              </DialogTitle>
              <DialogDescription>
                {isViewMode && viewingArtistDetail 
                  ? "View detailed information about this artist"
                  : editingArtistDetail 
                    ? "Update artist detail information" 
                    : "Create a new artist detail"
                }
              </DialogDescription>
            </DialogHeader>
            {isViewMode && viewingArtistDetail ? (
              <ViewArtistDetail
                artistDetail={viewingArtistDetail}
                onClose={() => setIsDialogOpen(false)}
                onEdit={() => {
                  setEditingArtistDetail(viewingArtistDetail);
                  setIsViewMode(false);
                }}
              />
            ) : (
              <ArtistForm
                editingArtist={editingArtistDetail}
                onSubmit={handleSubmit}
                onCancel={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Artists</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Reviewed</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.reviewed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.approved}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rejected</CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.rejected}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by ID, name, email, or city/state..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Artist Details Table */}
      <Card>
        <CardHeader>
          <CardTitle>Artist Details</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Loading artist details...</div>
            </div>
          ) : filteredArtistDetails.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No artist details found</h3>
              <p className="text-gray-500">
                {searchTerm || statusFilter !== "all" 
                  ? "Try adjusting your search or filters"
                  : "Get started by adding your first artist detail"
                }
              </p>
            </div>
          ) : (
            <ArtistDetailsTable
              artistDetails={filteredArtistDetails}
              onView={handleView}
              onDelete={handleDelete}
              onStatusChange={handleStatusChange}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
